import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Icon } from './Icon';
import './Toast.css';

/**
 * App-wide action feedback.
 *
 * The app had no toast/snackbar, so creates, edits and deletes succeeded silently (or only
 * with an inline state buried in one component). `useToast()` gives any component a
 * `toast.success/error/info(message)` that shows a dismissible, auto-expiring toast in a
 * corner stack. Outside the provider (or in a test) the hook returns no-ops so nothing
 * crashes.
 */
type ToastKind = 'success' | 'error' | 'info';

/** An inline button on a toast — e.g. Undo after a delete. */
export interface ToastAction { label: string; onClick: () => void }
export interface ToastOptions { action?: ToastAction }

interface ToastItem { id: number; kind: ToastKind; message: string; action?: ToastAction }

export interface ToastApi {
  success: (message: string, opts?: ToastOptions) => void;
  error: (message: string, opts?: ToastOptions) => void;
  info: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((kind: ToastKind, message: string, opts?: ToastOptions) => {
    const id = nextId++;
    const action = opts?.action;
    setToasts((prev) => [...prev.slice(-3), { id, kind, message, action }]); // cap the stack at ~4
    // A toast with an action (Undo) stays longer so there is time to reach for it;
    // an error stays longer than a plain confirmation.
    const ttl = action ? 8000 : kind === 'error' ? 6000 : 4000;
    window.setTimeout(() => remove(id), ttl);
  }, [remove]);

  const api = useMemo<ToastApi>(() => ({
    success: (m, opts) => push('success', m, opts),
    error: (m, opts) => push('error', m, opts),
    info: (m, opts) => push('info', m, opts),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-stack" role="region" aria-label="Notifications">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`} role="status" aria-live="polite">
            <span className="toast-icon">
              <Icon name={t.kind === 'success' ? 'checkCircle' : t.kind === 'error' ? 'warning' : 'bell'} size={16} />
            </span>
            <span className="toast-msg">{t.message}</span>
            {t.action && (
              <button
                type="button"
                className="toast-action"
                onClick={() => { remove(t.id); t.action!.onClick(); }}
              >
                {t.action.label}
              </button>
            )}
            <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => remove(t.id)}>
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const NOOP: ToastApi = { success: () => {}, error: () => {}, info: () => {} };

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP;
}
